import { Fragment, useEffect, useState } from 'react';
import { DEFAULT_ADVANCE_BUTTON_STYLE } from '../../../utils/styleHelpers';
import {
  getCurrentPreferences,
  getUser,
  GridPreferences,
  hasAllAccountScope,
  updateUserSettings,
  UserPreferences
} from '../../../store/User';
import { useSnackbar } from '../../../Context/SnackbarContext';
import { TextBlock, TextSpan } from '../../Atoms/Text';
import { ComponentSpinner } from '../Loading/ComponentSpinner';
import { CustomViewType, getGridDefsByType } from './helpers';
import {
  parseProjectScopedViewName,
  buildProjectScopedName,
  isProjectScopedType,
  isReservedAccountWideName,
  viewNameFromKey
} from './viewKey';
import { ScheduleSetting, useScheduleSettings } from '../../../store/ScheduleSettings';
import { SchedulerInline } from './SchedulerInline';
import { SharedSchedulesList } from './SharedSchedulesList';
import { OwnViewRow } from './OwnViewRow';
import { useSavedViews, View } from './useSavedViews';
import { DeleteViewDialog } from './DeleteViewDialog';
import { VIEW_ACTIONS_BLOCKED_REASONS, RENAME_NOT_SAFE_MESSAGE, viewNameTakenMessage } from './viewRenameMessages';
import { isScheduleOwner } from './scheduleOwnership';
import { viewKeysOf } from './scheduleTabs';
import { refusalMessage } from '../../../Api/ScheduleSettings/scheduleRefusalMessage';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';

export interface ViewsModalParams {
  open: boolean;
  handleClose: () => void;
  type: CustomViewType;
  // Present only on a single project's Related Inventory grid; scopes new schedules to that project.
  projectId?: string;
}

// A copy, so a request that fails leaves the live preferences as they were.
const editablePreferences = (): UserPreferences => {
  const current = getCurrentPreferences();
  return { ...current, content: { ...current.content } };
};

export const ViewsModal = ({ open, handleClose, type, projectId }: ViewsModalParams) => {
  const [views, setViews] = useSavedViews(type, projectId);
  const [name, setName] = useState<{ [key: number]: string }>({});
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { setSnack } = useSnackbar();
  const currentUser = getUser();

  const { schedules, fetchSchedules, saveSchedule, deleteSchedule, optOut, resume, runSchedule } =
    useScheduleSettings();
  const [activeSchedulerKey, setActiveSchedulerKey] = useState<string | null>(null);
  const [schedulesState, setSchedulesState] = useState<'loading' | 'ready' | 'failed'>('loading');
  const [pendingDelete, setPendingDelete] = useState<{ viewName: string; reports: string[] } | null>(null);

  useEffect(() => {
    if (!open) return;
    setActiveSchedulerKey(null);
    setSchedulesState('loading');
    Promise.resolve(fetchSchedules()).then((read) => setSchedulesState(read ? 'ready' : 'failed'));
  }, [open]);

  const handleEditClick = (index: number) => {
    const update: View[] = views.map((v, i) => (i === index ? { ...v, isEdit: true } : v));
    setViews(update);
    setName({ ...name, [index]: displayNameOf(update[index].name) });
  };

  const reloadSchedules = async () => {
    if (!(await fetchSchedules())) setSchedulesState('failed');
  };

  const handleDelete = async (viewName: string) => {
    setLoading(true);

    const currentSettings = editablePreferences();
    delete currentSettings.content[`${type}_grid_${viewName}`];
    const { data, error } = await updateUserSettings(currentSettings, `${type}_grid_${viewName}`);
    if (data) {
      await reloadSchedules();
      setSnack({ message: 'Your view was deleted successfully', type: 'success', open: true });
    }
    if (error)
      setSnack({ message: `There was an error deleting your view: \n${error.message}`, type: 'error', open: true });

    setLoading(false);
  };

  // A view that a report holds takes the report's tab with it, and a report left with no view is deleted. The list of
  // schedules belongs to the selected accounts, so a user who can reach others is warned even when it names none.
  const mayHaveOtherAccounts = hasAllAccountScope(currentUser) || (currentUser.accounts?.length ?? 0) > 1;
  const requestDelete = (viewName: string) => {
    const reports = reportsHoldingView(`${type}_grid_${viewName}`).map((report) => report.name);
    if (reports.length > 0 || mayHaveOtherAccounts) setPendingDelete({ viewName, reports });
    else handleDelete(viewName);
  };

  const handleSave = async (index: number) => {
    setLoading(true);
    const update: View[] = [...views];
    const currentViewName = update[index].name;
    const newName = name[index].trim();

    if (!newName) {
      setSnack({ message: 'The view needs a name.', type: 'error', open: true });
      setLoading(false);
      return;
    }

    // Same reservation as create: renaming an account-wide related-inventory view to a project-scoped
    // shape would move it off the account list onto that UUID's card. Refuse rather than reclassify.
    if (isReservedAccountWideName(type, projectId, newName)) {
      setSnack({ message: 'That view name is reserved. Please choose a different name.', type: 'error', open: true });
      setLoading(false);
      return;
    }

    // The user edits the bare name; a per-project view has to keep its UUID prefix in the stored key.
    const nextViewName = projectId ? buildProjectScopedName(projectId, newName) : newName;
    const previousViewKey = `${type}_grid_${currentViewName}` as const;
    const nextViewKey = `${type}_grid_${nextViewName}` as const;

    const renamed = previousViewKey !== nextViewKey;
    const holding = renamed ? reportsHoldingView(previousViewKey) : [];

    if (renamed && getCurrentPreferences().content[nextViewKey]) {
      setSnack({ message: viewNameTakenMessage(newName), type: 'error', open: true });
      setLoading(false);
      return;
    }

    // An API that does not move reports with a renamed view would delete the ones that hold it.
    if (holding.some((s) => !s.follows_view_rename)) {
      setSnack({ message: RENAME_NOT_SAFE_MESSAGE, type: 'error', open: true });
      setLoading(false);
      return;
    }

    const currentSettings: UserPreferences = editablePreferences();
    const currentView: Partial<GridPreferences> = { ...currentSettings.content[previousViewKey] };
    delete currentSettings.content[previousViewKey];

    currentSettings.content[nextViewKey] = {
      ...currentView,
      muiConfig: currentView.muiConfig || getGridDefsByType(type),
      disabledFields: currentView.disabledFields || []
    };

    const renamedViews = renamed ? { [previousViewKey]: nextViewKey } : undefined;
    const { data, error } = await updateUserSettings(currentSettings, nextViewKey, renamedViews);
    if (data) {
      await reloadSchedules();
      setSnack({ message: 'Your view was updated successfully', type: 'success', open: true });
    }
    if (error)
      setSnack({
        message: `There was an error updating your view: \n${refusalMessage(error, error.message)}`,
        type: 'error',
        open: true
      });
    setLoading(false);
  };

  const handleRunSchedule = async (id: string) => {
    // A refusal is reported where its cause is known; only the confirmation belongs to this screen.
    if (await runSchedule(id)) {
      setSnack({ message: 'Schedule sent to all recipients', type: 'success', open: true });
    }
  };

  const toggleScheduler = (key: string) => {
    setActiveSchedulerKey((prev) => (prev === key ? null : key));
  };

  // A view key carries no owner, so the user's own report leads the row when a colleague's report starts with the
  // same key.
  const getScheduleForViewKey = (viewKey: string) =>
    schedules.find((s) => s.view_key === viewKey && isScheduleOwner(s, currentUser.id)) ||
    schedules.find((s) => s.view_key === viewKey) ||
    null;

  // A view that is a tab of the user's own report is scheduled through that report, so its row does not offer a
  // schedule of its own.
  const reportsHoldingView = (viewKey: string) =>
    schedules.filter((s) => isScheduleOwner(s, currentUser.id) && viewKeysOf(s).includes(viewKey));

  // A view key can lead several of the user's reports: the first is the view's own row, the rest follow it.
  const ownReportsStartingWith = (viewKey: string) =>
    schedules.filter((s) => s.view_key === viewKey && isScheduleOwner(s, currentUser.id));

  // A per-project view stores its name UUID-prefixed; users only ever see/edit the bare name. Other view
  // types have no such prefix, so their names show verbatim.
  const displayNameOf = (rawName: string) => viewNameFromKey(`${type}_grid_${rawName}`);

  // A view key carries no owner, so a colleague's same-named view resolves here too — and their
  // pause reason describes their filter, not this row's.
  const isOwnSchedule = (schedule: ScheduleSetting | null) => isScheduleOwner(schedule, currentUser.id);

  // Schedules shared with current user (created by someone else, user is in recipient_portal_user_ids).
  // Bucketed by view-key prefix so a schedule shared for the other view type never crosses modes, and by
  // project scope so another project's (or account-wide) shared schedule never shows on this card.
  const viewKeyPrefix = `${type}_grid_`;
  const inScope = (viewKey: string) => {
    if (!isProjectScopedType(type)) return true;
    const parsed = parseProjectScopedViewName(viewKey.slice(viewKeyPrefix.length));
    return projectId ? parsed?.projectId === projectId : parsed === null;
  };
  const sharedSchedules = schedules.filter(
    (s) =>
      s.view_key.startsWith(viewKeyPrefix) &&
      inScope(s.view_key) &&
      !isScheduleOwner(s, currentUser.id) &&
      s.recipient_portal_user_ids.map(String).includes(String(currentUser.id))
  );

  const ownViewKeys = new Set(views.map((v) => `${viewKeyPrefix}${v.name}`));
  // Shared schedules, except those on one of the user's own views that the user's own report leads
  const sharedOnlySchedules = sharedSchedules.filter(
    (s) => !(ownViewKeys.has(s.view_key) && getScheduleForViewKey(s.view_key) === s)
  );

  const availableViews = views.map((view) => ({
    viewKey: `${viewKeyPrefix}${view.name}`,
    name: displayNameOf(view.name)
  }));

  const matchesSearch = (view: View) => {
    const query = searchQuery.toLowerCase();
    const viewKey = `${viewKeyPrefix}${view.name}`;
    const shown = getScheduleForViewKey(viewKey);
    const reportNames = [...reportsHoldingView(viewKey).map((report) => report.name), ...(shown ? [shown.name] : [])];
    return [displayNameOf(view.name), ...reportNames].some((text) => text.toLowerCase().includes(query));
  };

  const filteredViews = views.map((view, index) => ({ view, index })).filter(({ view }) => matchesSearch(view));
  const filteredSharedSchedules = sharedOnlySchedules.filter((s) =>
    s.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const hasNoResults = searchQuery.length > 0 && filteredViews.length === 0 && filteredSharedSchedules.length === 0;
  const hasNoViews = !searchQuery && views.length === 0 && filteredSharedSchedules.length === 0;

  const renderScheduler = (viewKey: string, viewName: string, existing: ScheduleSetting | null) => (
    <div className="pb-3">
      <SchedulerInline
        viewKey={viewKey}
        viewName={viewName}
        availableViews={availableViews}
        existingSchedule={existing}
        currentUserId={currentUser.id}
        currentUserEmail={currentUser.email}
        projectId={projectId}
        onSave={saveSchedule}
        onDelete={deleteSchedule}
        onOptOut={optOut}
        onResume={resume}
        onClose={() => setActiveSchedulerKey(null)}
      />
    </div>
  );

  return (
    <Dialog
      data-testid="views-modal"
      open={open}
      onClose={handleClose}
      fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', paddingBottom: '.7rem' }}>
        <TextBlock
          weight="bold"
          size="lg"
          color="grey9">
          Configure and Schedule Reports
        </TextBlock>
      </DialogTitle>
      <DialogContent
        sx={{ maxHeight: '70vh', overflow: 'auto', paddingTop: 'unset', paddingRight: '.7rem', paddingLeft: '.7rem' }}>
        {loading ? (
          <div className="mt-[5rem] items-center self-center">
            <ComponentSpinner label="Saving..." />
          </div>
        ) : (
          <>
            <TextField
              sx={{ marginLeft: '1rem', marginTop: '.5rem', width: '24rem' }}
              label="Search Views"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
            />

            {filteredViews.map(({ view, index }) => {
              const viewKey = `${type}_grid_${view.name}`;
              const schedule = getScheduleForViewKey(viewKey);
              const ownSchedule = schedule && isOwnSchedule(schedule) ? schedule : null;
              const holders = reportsHoldingView(viewKey).filter((report) => report.view_key !== viewKey);
              const furtherReports = ownSchedule
                ? ownReportsStartingWith(viewKey).filter((r) => r !== ownSchedule)
                : [];

              return (
                <Fragment key={`${view.name}-${index}`}>
                  <OwnViewRow
                    viewName={displayNameOf(view.name)}
                    schedule={schedule}
                    ownSchedule={ownSchedule}
                    holders={holders}
                    viewActions={{
                      isEditing: view.isEdit,
                      editedName: name[index],
                      blockedReason: VIEW_ACTIONS_BLOCKED_REASONS[schedulesState],
                      onEditedNameChange: (next) => setName({ ...name, [index]: next }),
                      onSaveName: () => handleSave(index),
                      onEdit: () => handleEditClick(index),
                      onDelete: () => requestDelete(view.name)
                    }}
                    onRunSchedule={handleRunSchedule}
                    onToggleScheduler={() => toggleScheduler(viewKey)}>
                    {activeSchedulerKey === viewKey && renderScheduler(viewKey, displayNameOf(view.name), schedule)}
                  </OwnViewRow>
                  {furtherReports.map((report) => (
                    <OwnViewRow
                      key={report.id}
                      viewName={displayNameOf(view.name)}
                      schedule={report}
                      ownSchedule={report}
                      holders={[]}
                      onRunSchedule={handleRunSchedule}
                      onToggleScheduler={() => toggleScheduler(`${viewKey}#${report.id}`)}>
                      {activeSchedulerKey === `${viewKey}#${report.id}` &&
                        renderScheduler(viewKey, displayNameOf(view.name), report)}
                    </OwnViewRow>
                  ))}
                </Fragment>
              );
            })}

            {filteredSharedSchedules.length > 0 && (
              <SharedSchedulesList
                schedules={filteredSharedSchedules}
                activeKey={activeSchedulerKey}
                currentUserId={currentUser.id}
                currentUserEmail={currentUser.email}
                onToggle={toggleScheduler}
                onSave={saveSchedule}
                onDelete={deleteSchedule}
                onOptOut={optOut}
                onResume={resume}
                onClose={() => setActiveSchedulerKey(null)}
              />
            )}
          </>
        )}
        {hasNoViews && !loading && (
          <div className="flex w-full justify-center mt-[5rem]">
            <TextSpan>No Views</TextSpan>
          </div>
        )}
        {hasNoResults && !loading && (
          <div className="flex w-full justify-center mt-[5rem]">
            <TextSpan>No views match &quot;{searchQuery}&quot;</TextSpan>
          </div>
        )}
      </DialogContent>
      <hr className="mb-1 mx-6" />
      <DialogActions sx={{ marginRight: 2, pb: 2 }}>
        <button
          className={DEFAULT_ADVANCE_BUTTON_STYLE}
          onClick={handleClose}>
          Close
        </button>
      </DialogActions>

      <DeleteViewDialog
        reports={pendingDelete?.reports ?? null}
        otherAccounts={mayHaveOtherAccounts}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          if (pendingDelete) handleDelete(pendingDelete.viewName);
          setPendingDelete(null);
        }}
      />
    </Dialog>
  );
};
